import type { Task } from "@app/core";
import { useTasksStore } from "../../stores/index.js";
import { blockDragState, occurrenceIsoOf, rescheduleTo, splitVirtualId } from "./drag.js";
import { dayStart } from "./month-helpers.js";
import { resolveSource } from "./resolve.js";
import { useRecurringTaskActions } from "./useRecurringTaskActions.js";

const RESIZE_STEP_MIN = 10;
/** Default duration assigned when an undated task (from the inbox or a parent's
 *  nested subtasks) is dropped onto the timed grid to be time-blocked. */
const DEFAULT_BLOCK_MIN = 60;
const ALL_DAY_MIN = 1440;

function snapDuration(minutes: number): number {
  return Math.max(RESIZE_STEP_MIN, Math.round(minutes / RESIZE_STEP_MIN) * RESIZE_STEP_MIN);
}

function allDayDuration(minutes: number | null | undefined): number {
  const n = Math.max(0, minutes ?? 0);
  return Math.max(ALL_DAY_MIN, Math.ceil(n / ALL_DAY_MIN) * ALL_DAY_MIN || ALL_DAY_MIN);
}

/** Task mutations triggered from the week/day grid (drag-move, resize). */
export function useCalendarMutations() {
  const edit = useTasksStore((s) => s.edit);
  const actions = useRecurringTaskActions();

  // Read tasks LIVE at call time, never from a closed-over snapshot. The grid's
  // memoized DayColumns ignore fn props and can skip re-rendering across an edit
  // (preserved DayCol refs), so a captured task list would be stale — dropping
  // onto such a column would `find` nothing and silently no-op. getState() always
  // sees the current store.
  async function onTaskResize(t: Task, newMinutes: number) {
    const source = resolveSource(t, useTasksStore.getState().tasks);
    await actions.resizeTask({ source, occKey: t.due_at, newMinutes: snapDuration(newMinutes), isVirtual: !!t.virtual });
  }

  async function onTaskResizeTop(t: Task, newStartIso: string, newMinutes: number) {
    const source = resolveSource(t, useTasksStore.getState().tasks);
    // Occurrence key = the series slot (RECURRENCE-ID) from the virtual id, not
    // the effective due_at — matches the reschedule-series path.
    const occKey = occurrenceIsoOf(t);
    if (!occKey) return;
    await actions.resizeTaskFromTop({ source, occKey, newStartIso, newMinutes: snapDuration(newMinutes), isVirtual: !!t.virtual });
  }

  async function handleDrop(day: Date, minutesFromMidnight: number, draggedId: string) {
    const date = new Date(day);
    date.setHours(Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60, 0, 0);
    const newIso = date.toISOString();

    const { sourceId, occIso } = splitVirtualId(draggedId);
    const source = useTasksStore.getState().tasks.find((t) => t.id === sourceId);
    if (!source) return;
    // Dropped back exactly where it started: nothing changed — no edit, no
    // recurring confirm. Compare against the dragged block's DISPLAYED start
    // (from the drag visual; an overridden occurrence renders away from its
    // RECURRENCE-ID slot, so occIso alone would miss). Read synchronously —
    // blockDragState is cleared on dragend, which fires after drop.
    const displayedIso = blockDragState()?.visual.task.due_at ?? occIso ?? source.due_at;
    if (displayedIso && new Date(displayedIso).getTime() === date.getTime()) return;
    // Time-blocking: an undated one-off (inbox item or nested subtask dragged out)
    // gets scheduled with a default block duration. Keeps subtask_parent_id, so a
    // detached subtask stays linked to its parent.
    if (!source.recurrence_id && !source.due_at) {
      const dur = source.duration_minutes > 0 ? source.duration_minutes : DEFAULT_BLOCK_MIN;
      await edit(source.id, { due_at: newIso, duration_minutes: dur });
      return;
    }
    // All-day → timed: convert to a time-blocked entry at the drop position.
    if (source.all_day) {
      await actions.convertTaskFromAllDay({
        source,
        oldOcc: occIso ?? source.due_at,
        newIso,
        durationMinutes: DEFAULT_BLOCK_MIN,
        isVirtual: occIso !== null,
      });
      return;
    }
    await actions.rescheduleTask({ source, oldOcc: occIso ?? source.due_at, newIso, isVirtual: occIso !== null });
  }

  async function handleAllDayDrop(day: Date, draggedId: string, durationMinutes?: number) {
    const { sourceId, occIso } = splitVirtualId(draggedId);
    const source = useTasksStore.getState().tasks.find((t) => t.id === sourceId);
    if (!source) return;

    const targetDay = dayStart(day);
    const newIso = targetDay.toISOString();

    // Time-blocking an undated one-off onto the all-day band schedules it as an
    // all-day entry, matching month and desktop all-day drops.
    if (!source.recurrence_id && !source.due_at) {
      await edit(source.id, {
        due_at: newIso,
        all_day: true,
        duration_minutes: allDayDuration(durationMinutes ?? source.duration_minutes),
      });
      return;
    }

    const oldOcc = occIso ?? source.due_at;
    if (source.all_day) {
      const movedIso = rescheduleTo(oldOcc, targetDay);
      if (oldOcc && movedIso === oldOcc) return;
      await actions.rescheduleTask({ source, oldOcc, newIso: movedIso, isVirtual: occIso !== null });
      return;
    }

    await actions.convertTaskToAllDay({
      source,
      oldOcc,
      newIso,
      durationMinutes: allDayDuration(durationMinutes ?? source.duration_minutes),
      isVirtual: occIso !== null,
    });
  }

  return { onTaskResize, onTaskResizeTop, handleDrop, handleAllDayDrop };
}
